import React, { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useReactTable, getCoreRowModel, getSortedRowModel, getExpandedRowModel, flexRender, ColumnDef, SortingState, ColumnOrderState, ColumnSizingState, ExpandedState, OnChangeFn, Row } from '@tanstack/react-table';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import Loader from './Loader';

export interface DataTableActionsConfig<TData> {
    header?: React.ReactNode;
    width?: number;
    zIndex?: number;
    render: (row: Row<TData>) => React.ReactNode;
}

export interface DataTableProps<TData> {
    data: TData[];
    columns: ColumnDef<TData, any>[];
    loading?: boolean;
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    columnOrder: ColumnOrderState;
    onColumnOrderChange: OnChangeFn<ColumnOrderState>;
    columnSizing: ColumnSizingState;
    onColumnSizingChange: OnChangeFn<ColumnSizingState>;
    expanded?: ExpandedState;
    onExpandedChange?: OnChangeFn<ExpandedState>;
    getSubRows?: (row: TData) => TData[] | undefined;
    lockedColumnIds?: string[];
    actions?: DataTableActionsConfig<TData>;
    maxHeight?: string;
    noDataMessage?: React.ReactNode;
    droppableId?: string;
    className?: string;
}

const DataTable = <TData,>({ data, columns, loading = false, sorting, onSortingChange, columnOrder, onColumnOrderChange, columnSizing, onColumnSizingChange, expanded, onExpandedChange, getSubRows, lockedColumnIds = [], actions, maxHeight = '70vh', noDataMessage = 'No Record Found', droppableId = 'data-table-columns', className = 'mt-2' }: DataTableProps<TData>) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef(0);

    const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
        scrollPositionRef.current = e.currentTarget.scrollTop;
    };

    useLayoutEffect(() => {
        if (!loading && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [loading, data]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnOrder,
            columnSizing,
            expanded: expanded ?? {},
        },
        onSortingChange,
        onColumnOrderChange,
        onColumnSizingChange,
        onExpandedChange: onExpandedChange ?? (() => { }),
        getSubRows,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        columnResizeMode: 'onChange',
        defaultColumn: {
            size: 150,
            minSize: 80,
            maxSize: 500,
        },
    });

    const headerGroup = table.getHeaderGroups()[0];
    const actionsWidth = actions?.width ?? 120;
    const totalSize = table.getTotalSize() + (actions ? actionsWidth : 0);

    const stickyHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        right: 0,
        zIndex: (actions?.zIndex ?? 1) + 1,
        padding: '10px 12px',
        fontSize: 12,
        fontWeight: 600,
        color: '#343A40',
        borderBottom: '1px solid #E9ECEF',
        borderLeft: '1px solid #E9ECEF',
        background: '#F8F9FA',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.05)',
        whiteSpace: 'nowrap',
    };

    const stickyBodyStyle: React.CSSProperties = {
        position: 'sticky',
        right: 0,
        zIndex: actions?.zIndex ?? 1,
        background: '#fff',
        borderLeft: '1px solid #E9ECEF',
        borderBottom: '1px solid #E9ECEF',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.05)',
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;
        const firstColumnLocked = lockedColumnIds.includes(columnOrder[0]);
        if (firstColumnLocked && (sourceIndex === 0 || destIndex === 0)) return;
        const newOrder = [...columnOrder];
        const [removed] = newOrder.splice(sourceIndex, 1);
        newOrder.splice(destIndex, 0, removed);
        onColumnOrderChange(newOrder);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {loading && <Loader />}
            <div
                ref={scrollContainerRef}
                onScroll={handleTableScroll}
                className={className}
                style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight,
                    border: '1px solid #E9ECEF',
                    borderRadius: 8,
                    width: '100%',
                    position: 'relative',
                    isolation: 'isolate',
                }}
            >
                <table style={{ width: '100%', minWidth: totalSize, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                    <colgroup>
                        {headerGroup.headers.map(header => (
                            <col key={header.id} style={{ width: header.getSize() }} />
                        ))}
                        {actions && <col style={{ width: actionsWidth }} />}
                    </colgroup>

                    <thead style={{ background: '#F8F9FA' }}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId={droppableId} direction="horizontal">
                                {provided => (
                                    <tr ref={provided.innerRef} {...provided.droppableProps}>
                                        {headerGroup.headers.map((header, index) => {
                                            const isLocked = lockedColumnIds.includes(header.column.id);
                                            return (
                                                <Draggable key={header.id} draggableId={header.id} index={index} isDragDisabled={isLocked}>
                                                    {(prov, snapshot) => {
                                                        const thStyle: React.CSSProperties = {
                                                            ...prov.draggableProps.style,
                                                            position: snapshot.isDragging ? (prov.draggableProps.style as any)?.position : 'sticky',
                                                            top: snapshot.isDragging ? (prov.draggableProps.style as any)?.top : 0,
                                                            padding: '10px 12px',
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#343A40',
                                                            borderBottom: '1px solid #E9ECEF',
                                                            borderRight: '1px solid #E9ECEF',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            background: snapshot.isDragging ? '#EEF0FD' : '#F8F9FA',
                                                            opacity: snapshot.isDragging ? 0.9 : 1,
                                                            boxShadow: snapshot.isDragging ? '0 4px 12px rgba(67,97,238,0.25)' : 'none',
                                                            zIndex: snapshot.isDragging ? 3 : 1,
                                                            width: header.getSize(),
                                                            boxSizing: 'border-box',
                                                            display: snapshot.isDragging ? 'inline-block' : undefined,
                                                        };

                                                        const thElement = (
                                                            <th ref={prov.innerRef} {...prov.draggableProps} style={thStyle}>
                                                                <div
                                                                    {...(!isLocked ? prov.dragHandleProps : {})}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        width: '100%',
                                                                        minWidth: 0,
                                                                        cursor: snapshot.isDragging ? 'grabbing' : isLocked ? 'default' : 'grab',
                                                                    }}
                                                                >
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    </span>

                                                                    {header.column.getCanSort() && (
                                                                        <span
                                                                            onClick={e => {
                                                                                e.stopPropagation();
                                                                                header.column.getToggleSortingHandler()?.(e);
                                                                            }}
                                                                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginLeft: 6, flexShrink: 0 }}
                                                                        >
                                                                            <i
                                                                                className={`bi ${header.column.getIsSorted() === 'asc'
                                                                                        ? 'bi-sort-up'
                                                                                        : header.column.getIsSorted() === 'desc'
                                                                                            ? 'bi-sort-down'
                                                                                            : 'bi-arrow-down-up'
                                                                                    }`}
                                                                                style={{ fontSize: 13, color: header.column.getIsSorted() ? '#4361EE' : '#C4C4C4' }}
                                                                            />
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {header.column.getCanResize() && (
                                                                    <div
                                                                        onMouseDown={e => {
                                                                            e.stopPropagation();
                                                                            header.getResizeHandler()(e);
                                                                        }}
                                                                        onTouchStart={e => {
                                                                            e.stopPropagation();
                                                                            header.getResizeHandler()(e);
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            right: 0,
                                                                            top: 0,
                                                                            height: '100%',
                                                                            width: 5,
                                                                            cursor: 'col-resize',
                                                                            userSelect: 'none',
                                                                            touchAction: 'none',
                                                                            background: header.column.getIsResizing() ? '#4361EE' : 'transparent',
                                                                        }}
                                                                    />
                                                                )}
                                                            </th>
                                                        );

                                                        return snapshot.isDragging ? ReactDOM.createPortal(thElement, document.body) : thElement;
                                                    }}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                        {actions && <th style={stickyHeaderStyle}>{actions.header ?? 'Actions'}</th>}
                                    </tr>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr
                                key={row.id}
                                style={{ background: '#fff' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FF')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        style={{
                                            padding: '10px 12px',
                                            borderRight: '1px solid #E9ECEF',
                                            borderBottom: '1px solid #E9ECEF',
                                            verticalAlign: 'middle',
                                            width: cell.column.getSize(),
                                            overflow: 'hidden',
                                            position: 'relative',
                                            zIndex: 0,
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                                {actions && (
                                    <td style={{ ...stickyBodyStyle, padding: '10px 12px', verticalAlign: 'middle' }}>
                                        {actions.render(row)}
                                    </td>
                                )}
                            </tr>
                        ))}
                        {!loading && table.getRowModel().rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.length + (actions ? 1 : 0)}
                                    className="text-center text-muted py-4"
                                    style={{ padding: '10px 12px', verticalAlign: 'middle', position: 'relative', zIndex: 0 }}
                                >
                                    <p className="noDataFound">{noDataMessage}</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default DataTable;